import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { postDelete, postList, postOrdering, postStatusChange } from "../../../../services/postService";
import Badge from "../../../../components/elements/Badge";
import { getPaginationRange, handleFilterChange } from "../../../../helpers";
import Table from "../../../../components/elements/table/Table";
import Button from "../../../../components/form/Button";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import TableControls from "../../../../components/elements/TableControls";
import ActionBar from "../../../../components/elements/ActionBar";
import { status_filter } from "../../../../components/constant/constant";
import TagFilter from "../../../../components/elements/TagFilter";
import Tooltip from "../../../../components/elements/Tooltip";
import { useLoadTaxonomyList } from "../../../../helpers/GetCommonList";
import FilterTags from "../../../../components/elements/FilterTags";
import { t } from "i18next";
import { orderList } from "../../../../services/orderService";
import StatusChangeTooltip from "../../../../components/common/StatusChangeTooltip";
import ReactPortal from "../../../../components/common/ReactPortal";
import OrderDetails from "./OrderDetails";
import Drawer from "../../../../components/drawer/Drawer";
import { tableList } from "../../../../services/tableService";

const Order = () => {
  setTitle("Order");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
  });

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectFilterItem, setSelectFilterItem] = useState([]);
  const [tableFilterData, setTableFilterData] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedAllFilters, setSelectedAllFilters] = useState([]);
  const userLang = localStorage.getItem("lang") || "en";

  const [selectedSearchData, setSelectedSearchData] = useState({
    category: [],
  });

  const { loadTaxonomyList } = useLoadTaxonomyList();
  const [categorieslist, setCategoriesList] = useState({
    data: [],
    loading: true,
  });
  const [categoryKeyWord, setCategoryKeyWord] = useState("");

  useEffect(() => {
    loadTaxonomyList({ type: "post-category", keyword: categoryKeyWord || "" }, setCategoriesList);
  }, [loadTaxonomyList, categoryKeyWord]);

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["order", filters, search, selectedSearchData?.category, selectFilterItem],
    queryFn: async () => {
      const res = await orderList({
        ...filters,
        ...(filters.status?.length > 0 && Object.fromEntries(filters.status.map((val, index) => [`status[${index}]`, val]))),
        ...(filters.tableNumber?.length > 0 && Object.fromEntries(filters.tableNumber.map((val, index) => [`tableNumber[${index}]`, val]))),
        ...(selectedFilter === "dateRange" && {
          startDate: dayjs(selectFilterItem?.value?.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(selectFilterItem?.value?.endDate).format("YYYY-MM-DD"),
        }),
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tableDataList } = useQuery({
    queryKey: ["tableList"],
    queryFn: async () => {
      const res = await tableList({ limit: 1000, page: 1 });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs;
    },
  });

  useEffect(() => {
    if (tableDataList?.data?.length > 0) {
      const uniqueTables = Array.from(new Set(tableDataList.data.map((item) => item?.tableNumber)));
      const options = uniqueTables.map((num) => ({
        name: `Table ${num}`,
        value: num,
        type: "checkbox",
      }));
      setTableFilterData(options);
    }
  }, [tableDataList]);

  const handleBadgeClick = (e, id) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    });
    setActiveTooltip((prev) => (prev === id ? null : id));
  };

  const statusArray = [
    {
      state: "paid",
      color: "success",
    },
    {
      state: "pending",
      color: "warning",
    },
    {
      state: "partial",
      color: "info",
    },
  ];

  const tableHeadData = [
    {
      _id: 1,
      name: "Order Id",
      align: "left",
    },
    {
      _id: 2,
      name: "Table Number",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            tableNumber: val.map((item) => item.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            const updated = { ...pre };
            delete updated.tableNumber;
            return updated;
          });
        }
      },
      filterData: tableFilterData || [],
    },
    {
      _id: 3,
      name: "Amount",
      align: "left",
    },
    {
      _id: 3,
      name: "Order Placed On",
      align: "left",
      isSearchFilter: true,
      onSearchFilter: (val) => {
        if (val?.startDate && val?.endDate) {
          setSelectedFilter("dateRange");
          setSelectFilterItem({
            value: {
              startDate: val.startDate,
              endDate: val.endDate,
            },
          });
          setFilters((prev) => ({ ...prev, page: 1 }));
        } else {
          setSelectedFilter(null);
          setSelectFilterItem(null);
        }
      },
      isOption: "range-picker",
    },
    {
      _id: 5,
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            status: val.map((item) => item.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            const updated = { ...pre };
            delete updated.status;
            return updated;
          });
        }
      },
      filterData: [
        { name: "Paid", value: "paid", type: "checkbox" },
        { name: "Pending", value: "pending", type: "checkbox" },
        { name: "Partial", value: "partial", type: "checkbox" },
      ],
    },
    {
      _id: 6,
      name: "Action",
      align: "left",
    },
  ];
  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              data-tooltip-id={`orderIdTooltip-${item?._id}`}
              data-tooltip-place="right"
              onClick={() => setSelectedOrderId(item?.uid)}
              className="text-gray-600 cursor-pointer hover:text-sm font-medium"
            >
              {item?.orderId}
            </div>
          ),
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div class="flex items-center gap-2 bg-[#224722]/10 text-[#224722] px-3 py-1 rounded-lg font-semibold w-fit">
              <i class="fa-solid fa-utensils"></i>
              <span>Table {item?.table?.tableNumber}</span>
            </div>
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <div className="flex flex-col">
              <span className={`text-${item?.totalPaid > 0 ? "gray" : "green"}-400 ${item?.totalPaid > 0 ? "line-through" : ""} text-sm`}>${item?.billAmount?.toFixed(2) || "-"}</span>
              {item?.totalPaid > 0 && (
                <>
                  <span className="text-blue-600 font-bold">Paid: ${item?.totalPaid?.toFixed(2) || "0.00"}</span>
                  <span className={`text-sm ${item?.billAmount - item?.totalPaid > 0 ? "text-red-500" : "text-green-600"}`}>
                    {item?.billAmount - item?.totalPaid > 0 ? `Remaining: $${(item?.billAmount - item?.totalPaid).toFixed(2)}` : "Payment has done"}
                  </span>
                </>
              )}
            </div>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Tooltip
              position="right"
              message={
                <div className="text-[12px] whitespace-pre-wrap max-w-xs">
                  {item?.createdAt
                    ? `Order placed at ${dayjs(item.lastLogin).format("DD MMMM, YYYY")} at ${dayjs(item.createdAt).format("hh:mm A")}.`
                    : "No order has been placed."}
                </div>
              }
            >
              {item?.createdAt ? dayjs(item?.createdAt).format("DD MMM, YYYY • hh:mm A") : "No login activity"}
            </Tooltip>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="relative">
              <div className="flex gap-2 items-center cursor-pointer" onClick={(e) => handleBadgeClick(e, item._id)}>
                <Badge
                  type={statusArray.find((st) => st.state === item?.status)?.color}
                  label={statusArray.find((st) => st.state === item?.status)?.state}
                />
              </div>
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Details",
              icon: "fa-solid fa-eye",
              onClick: () => setSelectedOrderId(item?.uid),
              standout: false,
            },
            ...(item?.status === "pending"
              ? [
                  {
                    _id: 2,
                    name: "Edit",
                    icon: "fa-regular fa-pen-to-square",
                    onClick: () => {
                      navigate(`/admin/manage-order/${item?.uid}`);
                    },
                    standout: true,
                  },
                ]
              : []),
            {
              _id: 3,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              // onClick: () => {
              //   setDeleteModal({
              //     isOpen: true,
              //     data: item,
              //   });
              // },
            },
          ],
        },
      ],
    };
  });

  //   order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await postDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["order", filters, search, selectFilterItem], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["order", filters, search, selectFilterItem]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  async function postOrderingHandler(newData, oldData) {
    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i,
      categoryType: "blog",
    }));
    const res = await postOrdering({ ordering_data });
    if (res?.status !== 200) {
      queryClient.setQueryData(["blog", filters, search], (state) => {
        if (state) {
          return { ...state, data: oldData };
        }
      });
      toast.warning(res?.message || res?.error);
    }
  }
  function actionHandler(val) {
    switch (val) {
      case "blogDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }
  const options = [
    {
      id: "blogDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Blog Delete",
    },
  ];

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        {/* {selectedAllFilters?.length > 0 && (
          <div className="flex gap-2 items-center flex-wrap mt-2">
            {selectedAllFilters?.map((item) => (
              <div
                key={item}
                className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                {item}
                <button
                  onClick={() => {
                    setSelectedAllFilters((prev) => prev.filter((i) => i !== item));
                    setSelectedFilter(null);
                    setSelectFilterItem({});
                  }}
                  className="text-blue-600 hover:text-red-500 focus:outline-none">
                  ×
                </button>
              </div>
            ))}
          </div>
        )} */}

        <div className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left">{`Orders`}</div>

        <div className="flex items-center gap-3 flex-col xls:flex-row w-full">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
          {/* <TagFilter
            type={"post"}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            setSelectFilterItem={setSelectFilterItem}
            setSelectedAllFilters={setSelectedAllFilters}
            filterOptions={[
              { name: "Status", value: "status" },
              { name: "Date Range", value: "dateRange" },
              { name: "Category", value: "category" },
              { name: "Tags", value: "tags" },
            ]}
          /> */}
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} Selected
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
          <div className="relative xls:ml-auto flex-shrink-0">
            <Button buttonIcon={"fa-regular fa-plus"} buttonLabel={"Add Orders"} buttonFunction={() => navigate("/admin/manage-order")} />
          </div>
        </div>
        {/* <FilterTags />  */}
      </div>

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(newData, oldData) => {
          postOrderingHandler(newData, oldData);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(data) =>
          queryClient.setQueryData(["blog", filters, search], (state) => {
            if (state) {
              return { ...state, data: data };
            }
          })
        }
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectable
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
      <Drawer
        title="Order Summary"
        subtitle="Manage and review all placed orders in one place."
        open={!!selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      >
        <OrderDetails orderId={selectedOrderId} />
      </Drawer>
    </div>
  );
};

export default Order;
